import React from 'react';
import { Award, ChefHat, Briefcase, Heart, BookOpen, Users } from 'lucide-react';
import { brandAssets } from '../utils/mockData';

const achievements = [
  {
    icon: Award,
    title: "Women Entrepreneur of the Year",
    subtitle: "Ministry of Biotechnology, Govt. of India (2024)",
  },
  {
    icon: Award,
    title: "Home Chef of the Year",
    subtitle: "Restaurant & Hotel Association of North India (2025)",
  },
];

const expertise = [
  {
    icon: ChefHat,
    title: "30+ Years of Experience",
    description: "Culinary arts mastery with interior design expertise",
  },
  {
    icon: Briefcase,
    title: "Multi-Cuisine Specialist",
    description: "Progressive Indian, Continental & authentic Bengali cuisine",
  },
  {
    icon: BookOpen,
    title: "Published Contributor",
    description: "Recipes featured in culinary literature",
  },
  {
    icon: Users,
    title: "Prestigious Consultancy",
    description: "Le Meridien, Hyatt, and luxury residential clubs",
  },
];

export const ChefSection = () => {
  return (
    <section id="chef" className="py-24 bg-gradient-to-b from-white to-[#EBEBD3]/20">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#59642C] mb-6">
              Meet Our Chef
            </h2>
            <div className="w-24 h-1 bg-[#FE9E1C] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600">
              Culinary excellence crafted by award-winning expertise
            </p>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
            {/* Chef Photo */}
            <div className="space-y-8">
              {/* Chef Image Card */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={brandAssets.chef}
                  alt="Chef Sonali Chatterji"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-[#FE9E1C] rounded-full flex items-center justify-center flex-shrink-0">
                      <ChefHat className="w-8 h-8 text-white" strokeWidth={2} />
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold text-white">Chef Sonali Chatterji</h3>
                      <p className="text-white/90 text-lg">Culinary Consultant & Caterer</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="bg-white p-8 rounded-3xl shadow-lg border border-[#EBEBD3]">
                <h4 className="text-2xl font-bold text-[#59642C] mb-4">Culinary Vision</h4>
                <p className="text-gray-700 leading-relaxed mb-4">
                  With over 30 years of distinguished experience, Chef Sonali brings a unique blend of culinary arts and interior design expertise to create exceptional dining experiences.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  As the force behind two acclaimed catering brands—<span className="font-semibold text-[#59642C]">Exotisch Global Cuisines</span> and <span className="font-semibold text-[#961B1F]">Paanch Phoran</span>—she brings unparalleled versatility to Balanced Bites Cafe.
                </p>
              </div>

              {/* Social Impact */}
              <div className="bg-gradient-to-br from-[#EBEBD3]/40 to-white p-8 rounded-3xl border border-[#EBEBD3]">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#961B1F]/10 rounded-xl flex items-center justify-center">
                    <Heart className="w-6 h-6 text-[#961B1F]" strokeWidth={2} />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-[#59642C] mb-2">
                      Recognized Humanitarian Effort
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      During COVID-19, Chef Sonali launched an initiative providing over 200 meals daily to patients and those in need—recognized by Al Jazeera and The New York Times.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Expertise & Awards */}
            <div className="space-y-8">
              {/* Expertise Grid */}
              <div>
                <h4 className="text-2xl font-bold text-[#59642C] mb-6">Expertise & Experience</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {expertise.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <div
                        key={index}
                        className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 border border-[#EBEBD3]"
                      >
                        <div className="w-12 h-12 bg-[#FE9E1C]/10 rounded-xl flex items-center justify-center mb-4">
                          <Icon className="w-6 h-6 text-[#FE9E1C]" strokeWidth={2} />
                        </div>
                        <h5 className="font-bold text-[#59642C] mb-2">{item.title}</h5>
                        <p className="text-sm text-gray-600">{item.description}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Awards */}
              <div>
                <h4 className="text-2xl font-bold text-[#59642C] mb-6">Prestigious Awards</h4>
                <div className="space-y-4">
                  {achievements.map((achievement, index) => {
                    const Icon = achievement.icon;
                    return (
                      <div
                        key={index}
                        className="bg-gradient-to-r from-[#FE9E1C]/10 to-white p-6 rounded-2xl border-l-4 border-[#FE9E1C] shadow-sm hover:shadow-md transition-all duration-300"
                      >
                        <div className="flex items-start space-x-4">
                          <div className="flex-shrink-0 w-12 h-12 bg-[#FE9E1C] rounded-full flex items-center justify-center">
                            <Icon className="w-6 h-6 text-white" strokeWidth={2} />
                          </div>
                          <div>
                            <h5 className="font-bold text-[#59642C] text-lg mb-1">
                              {achievement.title}
                            </h5>
                            <p className="text-gray-600 text-sm">{achievement.subtitle}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Consultancy Highlight */}
              <div className="bg-white p-8 rounded-3xl shadow-lg border border-[#EBEBD3]">
                <h4 className="text-xl font-bold text-[#59642C] mb-4">Notable Consultancy Work</h4>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <span className="text-[#FE9E1C] font-bold">•</span>
                    <span className="text-gray-700">Le Meridien Hotels (Delhi & Gurgaon) - Menu design & culinary strategy</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#FE9E1C] font-bold">•</span>
                    <span className="text-gray-700">Hyatt Centric, Chandigarh - Bengali food festivals</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#FE9E1C] font-bold">•</span>
                    <span className="text-gray-700">Luxury residential clubs - Curated dining experiences</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Bottom Statement */}
          <div className="text-center">
            <div className="inline-block bg-gradient-to-r from-[#59642C] to-[#59642C]/90 px-12 py-8 rounded-3xl shadow-2xl">
              <p className="text-2xl md:text-3xl font-bold text-white italic">
                "Every dish at Balanced Bites Cafe reflects our commitment to taste-first excellence"
              </p>
              <p className="text-white/80 mt-3 text-lg">— Chef Sonali Chatterji</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
